import React, { useState } from "react";
import { Grid, TextField, Box, Paper, Typography, InputAdornment, Switch } from "@mui/material";
import { UserPlus, User, Mail, Phone, MapPin, Lock, Eye, EyeOff } from 'lucide-react';
import * as actions from "./actions";
import ActionButton from '../../components/ActionButton';
import { toastError, toastSuccess } from "../../utils";

export default function UserActions(props) {
  const [createUserData, setCreateUserData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCreateUserData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onCreateUser = () => {
    if (
      !(
        createUserData.name &&
        createUserData.email &&
        createUserData.phone &&
        createUserData.address &&
        createUserData.password
      )
    ) {
      toastError("All fields are required");
      return;
    }
    props.setLoader(true);
    actions
      .addUserAndAssignToSubAdmin({
        name: createUserData.name,
        email: createUserData.email,
        phone: createUserData.phone,
        address: createUserData.address,
        password: createUserData.password,
      })
      .then((res) => {
        setCreateUserData({
          name: "",
          email: "",
          phone: "",
          address: "",
          password: "",
        });
        props.setLoader(false);
        toastSuccess(res.message || "Created successfully");
      })
      .catch((err) => {
        props.setLoader(false);
        toastError("Email already assigned");
      });
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: 'var(--bg-elevated)',
      '& fieldset': { borderColor: 'var(--border)' },
      '&:hover fieldset': { borderColor: 'var(--primary)' },
      '&.Mui-focused fieldset': { borderColor: 'var(--primary)' },
    },
    '& .MuiInputLabel-root': {
      color: 'var(--text-secondary)',
      '&.Mui-focused': { color: 'var(--primary)' },
    },
  };

  return (
    <Box sx={{ minHeight: 'calc(100vh - 140px)', pt: 4, position: 'relative' }}>
      {/* Availability Status - top right */}
      <Paper sx={{
        p: 2.5,
        backgroundColor: 'var(--bg-paper)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        position: 'absolute',
        top: 32,
        right: 0,
        width: 'fit-content',
        maxWidth: 360,
      }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: props.awayStatus ? '#F59E0B' : '#22C55E',
              boxShadow: props.awayStatus
                ? '0 0 8px rgba(245, 158, 11, 0.5)'
                : '0 0 8px rgba(34, 197, 94, 0.5)',
            }} />
            <Box>
              <Typography sx={{ color: '#F1F5F9', fontWeight: 600, fontSize: '0.85rem' }}>
                Availability Status
              </Typography>
              <Typography sx={{ color: '#64748B', fontSize: '0.72rem' }}>
                {props.awayStatus ? 'The create release feature for users is currently disabled' : 'The create release feature for users is currently enabled'}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1} sx={{ ml: 1 }}>
            <Typography sx={{ color: '#94A3B8', fontSize: '0.78rem', fontWeight: 500 }}>
              {props.awayStatus ? 'Away' : 'Available'}
            </Typography>
            <Switch
              checked={!props.awayStatus}
              onChange={props.onSetAwayText}
              sx={{
                width: 40,
                height: 22,
                p: 0,
                '& .MuiSwitch-switchBase': {
                  p: 0,
                  m: '3px',
                  transitionDuration: '250ms',
                  '&.Mui-checked': {
                    transform: 'translateX(18px)',
                    color: '#fff',
                    '& + .MuiSwitch-track': {
                      backgroundColor: '#22C55E',
                      opacity: 1,
                      border: 0,
                    },
                    '& .MuiSwitch-thumb': {
                      backgroundColor: '#fff',
                      boxShadow: '0 0 6px rgba(34, 197, 94, 0.4)',
                    },
                  },
                  '&.Mui-focusVisible .MuiSwitch-thumb': {
                    boxShadow: '0 0 0 4px rgba(108, 99, 255, 0.2)',
                  },
                },
                '& .MuiSwitch-thumb': {
                  width: 16,
                  height: 16,
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  transition: 'all 0.25s ease',
                },
                '& .MuiSwitch-track': {
                  borderRadius: 11,
                  backgroundColor: '#F59E0B',
                  opacity: 1,
                  transition: 'background-color 0.25s ease',
                },
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Create New User - centered */}
      <Grid container justifyContent="center" alignItems="center">
      <Grid item xs={12} sm={8} md={5} lg={4}>
        <Paper sx={{
          p: 4,
          backgroundColor: 'var(--bg-paper)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
        }}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Box sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              backgroundColor: 'rgba(108, 99, 255, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1.5,
            }}>
              <UserPlus size={20} color="#6C63FF" />
            </Box>
            <Typography sx={{ color: '#F1F5F9', fontWeight: 600, fontSize: '1.05rem' }}>
              Create New User
            </Typography>
            <Typography sx={{ color: '#64748B', fontSize: '0.78rem', mt: 0.5 }}>
              Add a new user and assign to your sub-admin
            </Typography>
          </Box>

          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Full Name"
              name="name"
              variant="outlined"
              size="small"
              value={createUserData.name}
              onChange={handleChange}
              fullWidth
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <User size={16} color="#64748B" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Email Address"
              name="email"
              type="email"
              variant="outlined"
              size="small"
              value={createUserData.email}
              onChange={handleChange}
              fullWidth
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Mail size={16} color="#64748B" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Phone Number"
              name="phone"
              type="text"
              variant="outlined"
              size="small"
              value={createUserData.phone}
              onChange={handleChange}
              fullWidth
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone size={16} color="#64748B" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Address"
              name="address"
              type="text"
              variant="outlined"
              size="small"
              value={createUserData.address}
              onChange={handleChange}
              fullWidth
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MapPin size={16} color="#64748B" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              size="small"
              value={createUserData.password}
              onChange={handleChange}
              fullWidth
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock size={16} color="#64748B" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" sx={{ cursor: 'pointer' }} onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff size={16} color="#64748B" /> : <Eye size={16} color="#64748B" />}
                  </InputAdornment>
                ),
              }}
            />

            <ActionButton variant="settle" onClick={onCreateUser} sx={{ gap: 1, py: 1, mt: 1 }}>
              <UserPlus size={16} /> Create User
            </ActionButton>
          </Box>
        </Paper>
      </Grid>
    </Grid>
    </Box>
  );
}
